import type { ModuleKey } from '@alicerce/contracts';
import type { ChargeView } from '@/components/payment-instructions';

export type Interval = 'MONTHLY' | 'YEARLY';
/** O gateway não tem tokenização de cartão no navegador; o painel cobra por Pix ou boleto. */
export type PanelPaymentMethod = 'PIX' | 'BOLETO';

export interface SubscriptionOverview {
  status: 'TRIALING' | 'ACTIVE' | 'PAST_DUE' | 'CANCELED';
  tenantStatus: 'TRIAL' | 'ACTIVE' | 'PAST_DUE' | 'READ_ONLY' | 'CANCELED';
  interval: Interval;
  nextInterval: Interval | null;
  trialEndsAt: string | null;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  pastDueSince: string | null;
  hasPaymentMethod: boolean;
  bundle: { key: string; name: string; discountPercent: number } | null;
  recurringTotalCents: number;
  items: { moduleKey: ModuleKey; name: string; status: 'ACTIVE' | 'PENDING_REMOVAL'; removeAt: string | null; totalCents: number }[];
}

export interface ModuleOption {
  key: ModuleKey;
  name: string;
  dependsOn: string[];
  active: boolean;
  amountCents: number | null;
  interval: Interval;
}

export interface ModulePreview {
  added: string[];
  addedDependencies: { moduleKey: string; requiredBy: string; reason: string }[];
  recurringDiffCents: number;
  prorataCents: number;
  remainingDays: number;
  cycleDays: number;
  quote: { interval: Interval; totalCents: number };
}

export interface AddModuleResult {
  status: 'ACTIVE' | 'PENDING_PAYMENT';
  added: string[];
  charge: ChargeView | null;
}

export interface Invoice {
  id: string;
  kind: 'SUBSCRIPTION' | 'PRORATA' | 'OVERAGE' | 'CREDITS';
  status: 'PENDING' | 'PAID' | 'OVERDUE' | 'CANCELED';
  amountCents: number;
  dueDate: string;
  paidAt: string | null;
  paymentUrl: string | null;
}

export interface PlatformFiscalDocument {
  id: string;
  kind: string;
  periodKey: string | null;
  status: string;
  number: string | null;
  amountCents: number;
  description: string;
  issuedAt: string | null;
  hasFiles: boolean;
}

export interface UsageLine {
  metric: string;
  label: string;
  used: number;
  included: number;
  percent: number;
  overageUnits: number;
  overageUnitCents: number;
  overageCents: number;
}

export interface CreditTransaction {
  id: string;
  type: 'PURCHASE' | 'SPEND' | 'FEE' | 'REFUND' | 'ADJUSTMENT';
  amountCents: number;
  balanceAfterCents: number;
  createdAt: string;
}

export const SUBSCRIPTION_STATUS: Record<SubscriptionOverview['tenantStatus'], { label: string; tone: 'success' | 'info' | 'warning' | 'danger' | 'neutral' }> = {
  TRIAL: { label: 'Período de teste', tone: 'info' },
  ACTIVE: { label: 'Ativa', tone: 'success' },
  PAST_DUE: { label: 'Pagamento atrasado', tone: 'warning' },
  READ_ONLY: { label: 'Somente leitura', tone: 'danger' },
  CANCELED: { label: 'Cancelada', tone: 'neutral' },
};

export const INVOICE_KIND: Record<Invoice['kind'], string> = {
  SUBSCRIPTION: 'Mensalidade',
  PRORATA: 'Módulo adicionado',
  OVERAGE: 'Consumo excedente',
  CREDITS: 'Créditos de mídia',
};

export const INVOICE_STATUS: Record<Invoice['status'], { label: string; tone: 'success' | 'warning' | 'danger' | 'neutral' }> = {
  PENDING: { label: 'Em aberto', tone: 'warning' },
  PAID: { label: 'Paga', tone: 'success' },
  OVERDUE: { label: 'Vencida', tone: 'danger' },
  CANCELED: { label: 'Cancelada', tone: 'neutral' },
};

export const CREDIT_TX: Record<CreditTransaction['type'], string> = {
  PURCHASE: 'Compra',
  SPEND: 'Gasto em anúncios',
  FEE: 'Taxa de gestão',
  REFUND: 'Estorno',
  ADJUSTMENT: 'Ajuste',
};

export const INTERVAL_LABEL: Record<Interval, string> = { MONTHLY: 'Mensal', YEARLY: 'Anual' };
